import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BookMarked, BookOpen, Home, RotateCcw } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/components/ui/cn'
import { useMinCopy } from '@/i18n/useUiLocale'
import { MobileMoreMenu } from '@/components/layout/MobileMoreMenu'
import { dashboardApi } from '@/api/dashboard'

export function MobileNav() {
  const { nav } = useMinCopy()
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.getDashboard(),
    staleTime: 1000 * 60,
  })

  const dueReviews = dashboard?.todayPlan.dueReviews ?? 0

  const navItems: Array<{ to: string; label: string; icon: LucideIcon; badge?: number }> = [
    { to: '/dashboard', label: nav.today, icon: Home },
    { to: '/vocabulary', label: nav.mobileWords, icon: BookMarked },
    { to: '/reading', label: nav.reading, icon: BookOpen },
    { to: '/review', label: nav.mobileReview, icon: RotateCcw, badge: dueReviews },
  ]

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card pb-[max(0.25rem,env(safe-area-inset-bottom))] md:hidden"
      aria-label={nav.mobileNavAria}
      data-testid="mobile-nav"
    >
      <div className="flex items-stretch justify-around">
        {navItems.map(({ to, label, icon: Icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'relative flex min-h-14 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            <span className="relative inline-flex">
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
              {badge != null && badge > 0 ? (
                <span
                  className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
                  data-testid="mobile-nav-review-badge"
                  aria-label={`${badge} due`}
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              ) : null}
            </span>
            <span className="leading-none">{label}</span>
          </NavLink>
        ))}
        <MobileMoreMenu />
      </div>
    </nav>
  )
}
